<template>
  <div class="cadence-timeline">
    <div v-if="!segments.length" class="empty">
      Choose an effective date to preview the transition.
    </div>
    <template v-else>
      <div class="strip">
        <div
          v-for="(seg, i) in segments"
          :key="i"
          class="seg"
          :class="seg.kind"
          :style="{ flexGrow: seg.days }"
          :title="seg.title"
        >
          <span class="seg-label">{{ seg.label }}</span>
          <span class="seg-days">{{ seg.daysLabel }}</span>
        </div>
      </div>
      <div class="legend">
        <span class="key current"></span> current cadence
        <span class="key orphan"></span> orphan / truncated
        <span class="key incoming"></span> new cadence
      </div>
    </template>
  </div>
</template>

<script>
// Renders the block layout across a cadence change as one proportional strip:
// the last blocks under the outgoing policy, any orphan window created at the
// seam, then the first blocks under the incoming policy.
//
// Segment width is proportional to day count, so a 2-day orphan next to two
// 14-day blocks reads as visibly wrong at a glance -- which is the entire
// point of the preview. Purely presentational: every date and day count comes
// from the preview response, so this component never does cadence math.
import { formatDate, formatSpanDays, parseDate } from "../utils/cadence";

export default {
  name: "CadenceTimeline",
  props: {
    // { start, end } ISO strings, end INCLUSIVE (as the preview returns them).
    outgoing: { type: Array, default: () => [] },
    orphan: { type: Object, default: null },
    incoming: { type: Array, default: () => [] },
  },
  computed: {
    segments() {
      const build = (block, kind) => {
        const start = parseDate(block.start);
        const end = parseDate(block.end);
        if (!start || !end) return null;
        // end is inclusive, so a Jul 20 - Aug 2 block spans 14 days.
        const days = Math.max(1, Math.round((end - start) / 86400000) + 1);
        return {
          kind,
          days,
          label: `${formatDate(start)} – ${formatDate(end)}`,
          daysLabel: formatSpanDays(days),
          title: `${block.start} to ${block.end} (${formatSpanDays(days)})`,
        };
      };

      const out = [];
      for (const b of this.outgoing || []) {
        const s = build(b, "current");
        if (s) out.push(s);
      }
      if (this.orphan) {
        const s = build(this.orphan, "orphan");
        if (s) out.push(s);
      }
      for (const b of this.incoming || []) {
        const s = build(b, "incoming");
        if (s) out.push(s);
      }
      return out;
    },
  },
};
</script>

<style scoped>
.cadence-timeline {
  width: 100%;
}

/* Field-shaped, matching the inputs it sits among. */
.empty {
  padding: 16px 12px;
  color: #666;
  font-size: 0.85em;
  background: #f7f8f9;
  border: 1px dashed #ccc;
  border-radius: 4px;
}

.strip {
  display: flex;
  align-items: stretch;
  gap: 3px;
  width: 100%;
  overflow-x: auto;
}

.seg {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  /* flex-grow carries the proportion; a basis of 0 makes the growth factor the
     only thing that decides width. min-width keeps a 2-day orphan legible
     rather than collapsing it to a sliver. */
  flex-basis: 0;
  min-width: 72px;
  padding: 9px 8px;
  border-radius: 4px;
  font-size: 0.76em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
}

.seg-label {
  font-weight: 600;
}

.seg-days {
  opacity: 0.75;
  font-size: 0.92em;
}

.seg.current {
  background: #e9ecef;
  color: #495057;
  border: 1px solid #dee2e6;
}

/* The orphan is the failure the preview exists to surface, so it is the one
   segment that reads as a warning rather than as data. */
.seg.orphan {
  background: repeating-linear-gradient(
    45deg,
    #fff3cd,
    #fff3cd 6px,
    #ffe9a8 6px,
    #ffe9a8 12px
  );
  color: #7a5b00;
  border: 1px solid #f0c36d;
}

.seg.incoming {
  background: #e7f1ff;
  color: #1c4e80;
  border: 1px solid #b6d4fe;
}

.legend {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.76em;
  color: #6b7280;
  flex-wrap: wrap;
}

.key {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-left: 10px;
}

.key:first-child {
  margin-left: 0;
}

.key.current {
  background: #e9ecef;
  border: 1px solid #dee2e6;
}

.key.orphan {
  background: #ffe9a8;
  border: 1px solid #f0c36d;
}

.key.incoming {
  background: #e7f1ff;
  border: 1px solid #b6d4fe;
}

@media (max-width: 768px) {
  .seg {
    min-width: 110px;
  }
}
</style>
